import type { Session, User } from "@supabase/supabase-js";
import { getSupabaseClient } from "./supabase-client.ts";

export type AccountSession = {
  email: string;
  loggedInAt: number;
  userId: string;
  accessToken: string;
};

export type AccountAuthResult =
  | { ok: true; session: AccountSession }
  | { ok: false; error: string };

function sessionFromSupabase(session: Session): AccountSession | null {
  const user = session.user;
  if (!user.email) {
    return null;
  }
  return {
    email: user.email,
    loggedInAt: Math.floor(Date.now()),
    userId: user.id,
    accessToken: session.access_token,
  };
}

function sessionFromUser(user: User, accessToken: string): AccountSession | null {
  if (!user.email) {
    return null;
  }
  return {
    email: user.email,
    loggedInAt: Math.floor(Date.now()),
    userId: user.id,
    accessToken,
  };
}

export async function loadAccountSession(): Promise<AccountSession | null> {
  const client = getSupabaseClient();
  const { data, error } = await client.auth.getSession();
  if (error || !data.session) {
    return null;
  }
  return sessionFromSupabase(data.session);
}

export async function signInWithPassword(
  email: string,
  password: string,
): Promise<AccountAuthResult> {
  const client = getSupabaseClient();
  const { data, error } = await client.auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  });
  if (error || !data.session) {
    return { ok: false, error: error?.message ?? "Sign-in failed" };
  }
  const session = sessionFromSupabase(data.session);
  if (!session) {
    return { ok: false, error: "Session is missing an email address" };
  }
  return { ok: true, session };
}

export async function signUpWithPassword(
  email: string,
  password: string,
): Promise<AccountAuthResult> {
  const client = getSupabaseClient();
  const { data, error } = await client.auth.signUp({
    email: email.trim().toLowerCase(),
    password,
  });
  if (error) {
    return { ok: false, error: error.message };
  }
  if (data.session) {
    const session = sessionFromSupabase(data.session);
    if (session) {
      return { ok: true, session };
    }
  }
  if (data.user) {
    // No active access token yet (email confirmation required).
    const session = sessionFromUser(data.user, "");
    if (session) {
      return { ok: true, session };
    }
  }
  return { ok: false, error: "Sign-up succeeded but no session was returned" };
}

export async function signOut(): Promise<void> {
  const client = getSupabaseClient();
  await client.auth.signOut();
}

export function subscribeToAuthChanges(
  onChange: (session: AccountSession | null) => void,
): () => void {
  const client = getSupabaseClient();
  const { data } = client.auth.onAuthStateChange((_event, session) => {
    onChange(session ? sessionFromSupabase(session) : null);
  });
  return () => {
    data.subscription.unsubscribe();
  };
}
